import "./App.css";
import React, { useMemo } from "react";
import { ThemeProvider } from "styled-components";
import useGameUpdate from "./hooks/useGameUpdate";
import useReducedMotion from "./hooks/useReducedMotion";
import Notifications from "./components/Notifications";
import Actions from "./components/Actions";
import Event from "./components/Event";
import Message from "./components/Message";
import Header from "./components/Header";
import Location from "./components/Location";
import { useGame } from "context/GameContext";

import Bag from "./components/Bag";
import Traits from "./components/Traits";
import ClockDebug from "./components/ClockDebug";
import GlobalTheme from "./components/Theme.style";
import { Backdrop, Closing, ClosingEdge, Page } from "./components/Page.style";
import { day, night } from "./theme";
import { HealthBands, healthBand } from "model/party/resources/health";
import { Clock as ClockConfig } from "config";

function App() {
  const { gameState, reset } = useGame();
  useGameUpdate(); // Starts the game loop when this component is mounted
  const reducedMotion = useReducedMotion();

  // The clock publishes a new gameState on every phase flip, so this re-evaluates with it.
  // Memoised because a fresh theme object on every render would restyle the whole tree.
  const isNight = gameState.calendar.isNight();
  const theme = useMemo(() => {
    const palette = isNight ? night : day;
    // The palette's `fade` is the crossover's duration; zeroing it here is the whole of the
    // reduced-motion story for everything that rides the shared clock. The flag rides along for
    // the two things that carry their own duration and so cannot read `fade` — the closing
    // vignette below and the header's guttering readout. See useReducedMotion.
    return reducedMotion ? { ...palette, fade: 0, reducedMotion: true } : palette;
  }, [isNight, reducedMotion]);

  // The vignette is scoped to the last band on purpose — see Page.style.js. Derived on every
  // render rather than memoised: health moves through resources.delta, which mutates in place and
  // publishes a new top-level gameState, so there is no stable dependency to key off.
  const closing = healthBand(gameState.party?.resources?.attribs?.health) === HealthBands.DYING;

  return (
    <ThemeProvider theme={theme}>
      <GlobalTheme />
      {/* Both of these stay mounted for the life of the app, which is what lets the day/night
          crossover transition at all — see Page.style.js. */}
      <Backdrop />
      <Page className="App">
        <Header />
        <menu>
          <Actions reset={reset} />
        </menu>
        <main>
          <Notifications />
          <div className="location">
            <Message />
            <Location />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <Bag />
            <Traits />
          </div>
          <Event />
        </main>
        {/* Discord Icon Link */}
        <a
          href="https://discord.gg/ncfRT7qt"
          rel="noopener noreferrer"
          target="_blank"
          className="discord-icon"
          aria-label="Join us on Discord"
        >
          discord.
        </a>
        {ClockConfig().showDebug && <ClockDebug />}
      </Page>
      {/* Stays mounted for the same reason Backdrop does: it has to have a live previous computed
          style for the transition to start from. It is inert and invisible above the last band. */}
      <Closing aria-hidden="true">
        <ClosingEdge $closed={closing} />
      </Closing>
    </ThemeProvider>
  );
}

export default App;
